import React, { useState, useEffect, useRef} from "react";
import axios from "axios";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DateTime } from "luxon";
import Select from "react-select";
import countries from "./countries"; 
import { useParams, useNavigate } from "react-router-dom"; // Import useNavigate
import CONFIG from "./config";

const PublicPage = () => {
    const { business_id } = useParams();
    const navigate = useNavigate(); // Hook for navigation
    const [businessData, setBusinessData] = useState(null);
    const [availability, setAvailability] = useState({}); // Stores raw availability with slots
    const [availableDates, setAvailableDates] = useState([]); // Stores only the dates for calendar
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sentVerificationCode, setSentVerificationCode] = useState(false);
    const [enteredCode, setEnteredCode] = useState("");
    const [services, setServices] = useState([]);
    const [customerInfo, setCustomerInfo] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
        streetAddress: "",
        additionalInfo: "",
    });    
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [redirectError, setRedirectError] = useState("");
    const [redirectSuccess, setRedirectSuccess] = useState("");
    const [reqCusInfo, setReqCusInfo] = useState({ name: true, email: true, phone: false, address: false });
    const bookingSectionRef = React.useRef(null);
    const [isChecked, setIsChecked] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [businessTimezone, setBusinessTimezone] = useState("");
    const [customerTimezone, setCustomerTimezone] = useState(window.Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [timezone, setTimezone] = useState(customerTimezone);
    const calendarRef = useRef(null);
    const serviceRef = useRef(null);


    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/enterprise.js?render=' + CONFIG.siteKey;
        script.async = true;
        document.body.appendChild(script);
        return () => { document.body.removeChild(script); };
    }, []);

    useEffect(() => {
        fetchBusinessData();
        fetchServices();
        fetchAvailability(30, null);
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const errorParam = queryParams.get("error");
        const successParam = queryParams.get("success");
      
        if (errorParam) {
          setRedirectError(errorParam);
        }
        if (successParam) {
          setRedirectSuccess(successParam);
        }
        // Remove the query parameters from the URL without reloading the page
        const newUrl = window.location.pathname;
        window.history.replaceState(null, "", newUrl);
      }, []);      

    
    useEffect(() => {
        const initializeServiceAndAvailability = async () => {
            // Fetch business data
            const servicesData = await fetchServices();
    
            if (servicesData && servicesData.length > 0) {
                // Find the service with the smallest duration
                const smallestDurationService = servicesData.reduce((minService, currentService) => {
                    return currentService.duration < minService.duration ? currentService : minService;
                });
    
                setSelectedService(smallestDurationService); // Set the initial service
    
                // Fetch availability for the smallest duration
                await fetchAvailability(smallestDurationService.duration,smallestDurationService.service_id);
            } else {
                // Default fetch if no services are available
                await fetchAvailability(30,null);
            }
        };
    
        initializeServiceAndAvailability();
    }, []); // Empty dependency array ensures this runs only once on load
    
    
    useEffect(() => {
        if (availableSlots.length > 0 && !selectedDate) {
            // Set the first available slot as default selected date
            setSelectedDate(DateTime.fromISO(availableSlots[0], { zone: timezone || "UTC" }).toJSDate());
        }
    }, [availableSlots, selectedDate]); // Run when availableSlots changes

    // Modified fetchBusinessData to return data
    const fetchBusinessData = async () => {
        try {
            const response = await axios.get(`${CONFIG.BACKEND_URL}/api/public/${business_id}`);
            const fetchedData = response.data;
    
            setBusinessData(fetchedData);
            setBusinessTimezone(fetchedData.timezone);
    
            // Initialize required customer info state based on fetched data
            setReqCusInfo(fetchedData.req_cus_info || { name: true, email: true, phone: false, address: false });
    
            return fetchedData; // Return fetched data for immediate use
        } catch (err) {
            console.error("❌ Error fetching business data:", err);
            // Redirect if business is not found (404) or not published
            if (err.response && err.response.status === 404) {
                navigate("/"); // Redirect to login page
            } else {
                setError("Failed to fetch business data.");
            }
        }
    };
    
    const fetchServices = async () => {
        const response = await axios.get(`${CONFIG.BACKEND_URL}/api/services_public/${business_id}`);
        setServices(response.data.services || []);
        return response.data.services || [];
    };
    
    const fetchAvailability = async (duration = 30, serviceId = null) => {
        if (!serviceId) {
            setError("Please select a service first");
            return { rawAvailability: {}, availableDates: [] };
        }

        try {
            const response = await axios.get(
                `${CONFIG.BACKEND_URL}/api/availability/${business_id}`,
                { 
                    params: { 
                        duration,
                        service_id: serviceId
                    }
                }
            );

            const rawAvailability = response.data.availability || {};
            const businessTimezone = timezone || "UTC";

            // Extract available dates for calendar view
            const availableDates = Object.keys(rawAvailability).filter(
                (date) => rawAvailability[date].length > 0
            );

            setAvailability(rawAvailability);
            setAvailableDates(availableDates.map((date) => 
                DateTime.fromISO(date, { zone: businessTimezone }).toISODate()
            ));

            return { rawAvailability, availableDates };
        } catch (err) {
            console.error("Error fetching availability:", err);
            setError("Failed to fetch availability.");
            return { rawAvailability: {}, availableDates: [] };
        }
    };

    const handleDateClick = async (info) => {
        setError("");
        const businessTimezone = timezone || "UTC";
    
        const clickedDate = DateTime.fromFormat(info.dateStr, "yyyy-MM-dd", { zone: businessTimezone }).toISODate();
    
        try {
            const duration = selectedService?.duration || 30;
            const { rawAvailability } = await fetchAvailability(duration, selectedService.service_id);
    
    
            const rawSlots = rawAvailability[clickedDate] || [];
    
            const convertedSlots = rawSlots.map((utcSlot) =>
                DateTime.fromISO(utcSlot, { zone: "UTC" }).setZone(businessTimezone).toISO()
            );

            // ✅ Properly handle empty slots before setting selectedDate
            if (convertedSlots.length > 0 && convertedSlots[0]) {
                setSelectedDate(DateTime.fromISO(convertedSlots[0], { zone: businessTimezone }).toJSDate());
            } else {
                setSelectedDate(DateTime.fromFormat(clickedDate, "yyyy-MM-dd", { zone: businessTimezone }).toJSDate());
            }
            
            setAvailableSlots(convertedSlots);

            // Defer scrolling until DOM updates
            setTimeout(() => {
                if (bookingSectionRef.current) {
                bookingSectionRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }, 0);

            // ✅ Auto-scroll to the booking section
            if (bookingSectionRef.current) {
                bookingSectionRef.current.scrollIntoView({ behavior: "smooth" });
            }
        } catch (err) {
            console.error("❌ Error fetching slots for date click:", err);
            setError("Failed to fetch available slots for the selected date.");
        }
    };
    
    const handleServiceChange = async (service) => {
        setSelectedService(service);
        setError("");
        setAvailableSlots([]); // Clear existing slots when service changes

        if (service) {
            try {
                const { rawAvailability } = await fetchAvailability(
                    service.duration,
                    service.service_id
                );

                if (selectedDate) {
                    const businessTimezone = timezone || "UTC";
                    const dateKey = DateTime.fromJSDate(selectedDate, { zone: businessTimezone }).toISODate();
                    const updatedSlots = (rawAvailability[dateKey] || []).map(slot =>
                        DateTime.fromISO(slot, { zone: "UTC" }).setZone(businessTimezone).toISO()
                    );
                    setAvailableSlots(updatedSlots);
                    console.log("Updated Slots:", updatedSlots);
                }
            } catch (err) {
                console.error("Error updating availability:", err);
                setError("Failed to update availability.");
            }
        }
    };
    
    

    const handleBookingSubmit = async () => {
        // Clear any existing error or success messages
        setError("");
        setSuccessMessage("");

        if (!enteredCode) {
            setError("Please enter the verification code you received via email.");
            return;
          }

        if (!isChecked) {
            setError('You must agree to the privacy terms to submit your request.');
            return;
          }

        if (!selectedService || !selectedDate) {
            setError("Please select a service and time slot.");
            return;
        }

        // Email validation regex
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (businessData?.req_cus_info?.email && !emailPattern.test(customerInfo.email)) {
            setError("Please enter a valid email address.");
            return;
        }
    
        const requiredFields = {
            name: businessData?.req_cus_info?.name || false,
            email: businessData?.req_cus_info?.email || false,
            phone: businessData?.req_cus_info?.phone || false,
            address: businessData?.req_cus_info?.address || false,
        };
        
    
        // Validate required fields
        if (
            (requiredFields.name && !customerInfo.name) ||
            (requiredFields.email && !customerInfo.email) ||
            (requiredFields.phone && !customerInfo.phone) ||
            (requiredFields.address &&
                (!customerInfo.streetAddress ||
                    !customerInfo.city ||
                    !customerInfo.state ||
                    !customerInfo.country ||
                    !customerInfo.zipCode))
        ) {
            setError("Please fill in all required fields.");
            return;
        }

        // ✅ Prevent multiple submissions
        if (isSubmitting) return;
        setIsSubmitting(true); // Disable the button
    
        // Merge the full address into a single formatted string if required
        let fullAddress = "";
        if (requiredFields.address) {
            fullAddress = `${customerInfo.streetAddress}, ${customerInfo.city}, ${customerInfo.state}, ${customerInfo.country}, ${customerInfo.zipCode}`;
        }
    
        try {
            const businessTimezone = timezone || "UTC";
            console.log("Selected Date:", selectedDate);
            console.log("Selected Service:", selectedService);
            console.log("Selected Service Duration:", selectedService.duration);
            // ✅ Convert selected date from business timezone to UTC
            const startDateTimeUTC = DateTime.fromJSDate(selectedDate, { zone: businessTimezone }).toUTC().toISO();
            const endDateTimeUTC = DateTime.fromJSDate(
                new Date(selectedDate.getTime() + selectedService.duration * 60000),
                { zone: businessTimezone }
            ).toUTC().toISO();
    
            // Check capacity before booking
            const availabilityResponse = await axios.get(
                `${CONFIG.BACKEND_URL}/api/availability/${business_id}`,
                { 
                    params: { 
                        duration: selectedService.duration,
                        service_id: selectedService.service_id
                    }
                }
            );
    
            const updatedAvailability = availabilityResponse.data.availability || {};
            const selectedDateKey = DateTime.fromJSDate(selectedDate, { zone: businessTimezone }).toISODate();
            const updatedSlots = updatedAvailability[selectedDateKey] || [];
    
            if (!updatedSlots.includes(startDateTimeUTC)) {
                setError("The selected time slot is no longer available for this service. Please choose another time.");
                return;
            }
    
            // Proceed with booking
            const bookingData = {
                name: requiredFields.name ? customerInfo.name : "",
                email: requiredFields.email ? customerInfo.email : "",
                phone: requiredFields.phone ? customerInfo.phone : "",
                address: requiredFields.address ? fullAddress : "",
                service: selectedService,
                timeSlot: startDateTimeUTC, // Converted to UTC
                endTime: endDateTimeUTC, // Converted to UTC
                additionalInfo: customerInfo.additionalInfo || "",
                verificationCode: enteredCode,
                service_id: selectedService.service_id,
                num_of_customer: 1
            };

            const token = await window.grecaptcha.enterprise.execute(CONFIG.siteKey, { action: 'submit' });
            
            const response = await axios.post(`${CONFIG.BACKEND_URL}/api/public/${business_id}/book`, 
                    { ...bookingData, 
                        recaptchaToken: token,
                        action: 'submit'
                     });
            setSuccessMessage(response.data.message || "Booking submitted successfully!");
            setError("");
            setSentVerificationCode(false);
            setEnteredCode("");
        } catch (err) {
            console.error("Error during booking:", err);
            setError("Failed to complete booking. Please try again.");
        } finally {
            setIsSubmitting(false); // ✅ Re-enable the button
        }
    };
    
    // Step 1: Send code
    const handleSendCode = async () => {
        setError("");
        setSuccessMessage("");
        setIsButtonDisabled(true); // Disable button
    
        if (!isChecked) {
            setError('You must agree to the privacy terms to submit your request.');
            setIsButtonDisabled(false); // Enable button
            return;
          }

        if (!selectedService || !selectedDate) {
            setError("Please select a service and time slot.");
            setIsButtonDisabled(false); // Enable button
            return;
        }

        // Email validation regex
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (businessData?.req_cus_info?.email && !emailPattern.test(customerInfo.email)) {
            setError("Please enter a valid email address.");
            setIsButtonDisabled(false); // Enable button
            return;
        }
    
        const requiredFields = {
            name: businessData?.req_cus_info?.name || false,
            email: businessData?.req_cus_info?.email || false,
            phone: businessData?.req_cus_info?.phone || false,
            address: businessData?.req_cus_info?.address || false,
        };
        
    
        // Validate required fields
        if (
            (requiredFields.name && !customerInfo.name) ||
            (requiredFields.email && !customerInfo.email) ||
            (requiredFields.phone && !customerInfo.phone) ||
            (requiredFields.address &&
                (!customerInfo.streetAddress ||
                    !customerInfo.city ||
                    !customerInfo.state ||
                    !customerInfo.country ||
                    !customerInfo.zipCode))
        ) {
            setError("Please fill in all required fields.");
            setIsButtonDisabled(false); // Enable button
            return;
        }

        
    
        try {
        const token = await window.grecaptcha.enterprise.execute(CONFIG.siteKey, { action: 'submit' });

        const businessTimezone = timezone || "UTC";
        const startDateTimeUTC = DateTime.fromJSDate(selectedDate, { zone: businessTimezone }).toUTC().toISO();
        
        await axios.post(`${CONFIG.BACKEND_URL}/api/public/${business_id}/send-code`, {
            recaptchaToken: token,
            action: 'submit',
            email: customerInfo.email,
            service: selectedService,
            timeSlot: startDateTimeUTC,
            businessName: businessData?.name || "Rili Day" // Or pass whichever name you want
        });

        setSentVerificationCode(true);
        setError("");
        setCountdown(60); // Set countdown to 60 seconds

        // Start the countdown timer
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer); // Stop timer when it reaches 0
                    setIsButtonDisabled(false); // Enable button
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    
        setSuccessMessage("Verification code has been sent to your email. \nPlease enter it below before final submission. \nCheck the spam/junk mail if you don't see it.");
        } catch (err) {
        console.error("Send code error:", err);
        setError(err.response?.data?.error || "Failed to send verification code.");
        setIsButtonDisabled(false); // Enable button
        }
    };
    
    
    
    if (!businessData) return null; // Prevent rendering empty page before redirect

    return (
        <div>
            <div style={styles.container}>
                {redirectError && <p style={styles.error}>{redirectError}</p>}
                {redirectSuccess && <p style={styles.success}>{redirectSuccess}</p>}

                {businessData && (
                    <>
                        <h1 style={styles.title}>{businessData.name}</h1>
                        <p style={styles.description}>
                            {businessData.description}
                        </p>
                        {availableSlots && services && services.length > 0 && (
                            <button style={styles.button} onClick={() => {
                                serviceRef.current.scrollIntoView({ behavior: "smooth" });
                            }}>Book an Appointment</button>
                        )}<br /><br />
                        
                        {businessData.business_phone && (
                        <p><strong>Phone:</strong> {businessData.business_phone}</p>
                        )}
                        {businessData.business_email && (
                        <p><strong>Email:</strong> {businessData.business_email}</p>
                        )}
                        {businessData.business_address && (
                        <p><strong>Address:</strong> {businessData.business_address}</p>
                        )}  
                    </>
                )}

                        {services && services.length > 0 && (
                            <>
                                <p ref={serviceRef}><strong>Select a service</strong></p>
                                <div style={styles.servicesList}>
                                    {services.map((service, index) => (
                                        <div 
                                            key={index} 
                                            style={styles.serviceCard}
                                            onClick={() => {
                                                handleServiceChange(service);
                                                calendarRef.current.scrollIntoView({ behavior: "smooth" });
                                            }}
                                        >
                                            <div style={styles.serviceInfo}>
                                                <p style={styles.serviceName}><strong>{service.name}</strong></p>
                                                <span style={styles.serviceDuration}>{service.duration} minutes</span>
                                            </div>
                                            {service.description && (
                                                <p style={styles.serviceDescription}>
                                                    {service.description}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                {availableSlots && services && services.length > 0 && (
                <div style={styles.calendarSection} ref={calendarRef}>
                    <p style={styles.startPrompt}>Click
                        <span style={styles.colorBlock}>  
                        </span> 
                        on the calendar to select a date and time
                    </p>

                    <FullCalendar
                        plugins={[dayGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        height="auto"
                        headerToolbar={{
                            left: "prev,next today",
                            center: "",
                            right: "title"
                        }}
                        titleFormat={{ month: "long" }}  // ✅ Show only the month name (e.g., "February")
                        dayHeaderFormat={{ weekday: 'narrow' }}
                        events={availableDates.map((date) => ({
                            title: "",
                            start: date, // Only date (no time)
                            display: "background",
                            //backgroundColor: "rgba(14, 111, 1, 1)", // Override global color
                            //borderColor: "transparent",
                        }))}
                        dateClick={handleDateClick}
                    />
                </div>
                )}

                {selectedDate && (
                    <div style={styles.bookingSection} ref={bookingSectionRef}>
                        {services && services.length > 0 && (
                        <>
                        <label style={styles.label}>Change timezone:</label>
                        <select
                            value={timezone}
                            onChange={(e) => {
                                const newTimezone = e.target.value;
                                console.log('Selected Timezone:', newTimezone);
                                setTimezone(newTimezone);
                                fetchAvailability(selectedService.duration, selectedService.service_id);
                            }}
                            style={{marginBottom: '20px'}}
                        >
                        
                            <option key={1} value={customerTimezone}>Your local timezone: {customerTimezone}</option>
                            <option key={2} value={businessTimezone}>Business timezone: {businessTimezone}</option>
                        
                        </select>
                        </>
                        )}
                        
                        <label style={styles.label}>
                            Date selected:{" "}
                            {selectedDate.toLocaleDateString([], {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </label>
                        <br />
                        <label style={styles.label}>Change service</label>
                        <select
                            style={styles.input}
                            value={selectedService ? selectedService.name : ""}
                            onChange={(e) => {

                                const selectedServiceName = e.target.value;
                                if (selectedServiceName) {
                                    const service = services.find(service => service.name === selectedServiceName);
                                    if (service) {
                                        setSelectedService(service);
                                        handleServiceChange(service); // Manually trigger handleServiceChange
                                    }
                                } else {
                                    console.log("⚠️ No service selected");
                                }
                            }}
                        >
                            {services.map((service, index) => (
                                <option key={index} value={service.name}>
                                    {service.name} - {service.duration} minutes
                                </option>
                            ))}
                        </select>

                        <label style={styles.label}>Available Slots</label>
                        {availableSlots.length > 0 ? (
                            <select
                                style={styles.input}
                                value={selectedDate ? DateTime.fromJSDate(selectedDate).toISO() : ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value) {
                                        // Convert selected slot to a JavaScript Date in the business timezone
                                        const selectedSlot = DateTime.fromISO(value, { zone: timezone || "UTC" }).toJSDate();
                                        
                                        setSelectedDate(selectedSlot); // Update selected date
                                    } else {
                                        console.log("⚠️ No slot selected.");
                                    }
                                }}
                            >
                                {availableSlots.map((slot, index) => (
                                    <option key={index} value={slot}>
                                        {DateTime.fromISO(slot, { zone: timezone || "UTC" }).toFormat("hh:mm a")}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <p style={styles.error}>No available slots for this date.</p>
                        )}
                        {reqCusInfo.name && (
                            <>
                                <label style={styles.label}>Name</label>
                                <input
                                    type="text"
                                    placeholder="Required"
                                    style={styles.input}
                                    value={customerInfo.name}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                                    required
                                />
                            </>
                        )}

                        {/* Email is always required */}
                                <label style={styles.label}>Email</label>
                                <input
                                    type="email"
                                    placeholder="Required"
                                    style={styles.input}
                                    value={customerInfo.email}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                                />

                        {reqCusInfo.phone && (
                            <>
                                <label style={styles.label}>Phone</label>
                                <input
                                    type="tel"
                                    placeholder="Required"
                                    style={styles.input}
                                    value={customerInfo.phone}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value.replace(/\D/g, '') })}
                                    required
                                />
                            </>
                        )}

                        {businessData?.req_cus_info?.address && (
                            <div>
                                <label style={styles.label}>Address</label>
                                <label style={styles.label}>Country</label>
                                <Select
                                    options={countries}
                                    value={countries.find(c => c.value === customerInfo.country)}
                                    onChange={(selected) => {
                                        setCustomerInfo({ ...customerInfo, country: selected.value, state: "" });
                                    }}
                                />

                                {customerInfo.country && (
                                    <>
                                        <label style={{...styles.label, marginTop: '10px'}}>State/Province</label>
                                        <Select
                                            options={countries.find(c => c.value === customerInfo.country)?.states || []}
                                            value={countries.find(c => c.value === customerInfo.country)?.states.find(s => s.value === customerInfo.state)}
                                            onChange={(selected) => setCustomerInfo({ ...customerInfo, state: selected.value })}
                                            styles={customSelectStyles}
                                        />
                                    </>
                                )}
                                <label style={{...styles.label, marginTop: '10px'}}>City</label>
                                <input
                                    type="text"
                                    placeholder="Required"
                                    style={styles.input}
                                    value={customerInfo.city}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                                    required
                                />

                                <label style={styles.label}>Street Address</label>
                                <input
                                    type="text"
                                    placeholder="Required"
                                    style={styles.input}
                                    value={customerInfo.streetAddress}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, streetAddress: e.target.value })}
                                    required
                                />

                                <label style={styles.label}>Zip/Postal Code</label>
                                <input
                                    type="text"
                                    style={styles.input}
                                    value={customerInfo.zipCode}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, zipCode: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        <label style={styles.label}>Additional Information</label>
                        <textarea
                            style={styles.input}
                            placeholder="Optional"
                            value={customerInfo.additionalInfo}
                            onChange={(e) => setCustomerInfo({ ...customerInfo, additionalInfo: e.target.value })}
                        />
                        <br />
                        <div style={{ marginBottom: '20px', marginTop: '0px' }}>
                            <input type="checkbox" checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} required />
                            <span> Check to agree with the 
                                {" "}
                                <a href={`${CONFIG.FRONTEND_URL}/privacy`} target="_blank" rel="noopener noreferrer">
                                    privacy terms
                                </a>
                                {" "}and{" "}
                                <a href={`${CONFIG.FRONTEND_URL}/terms`} target="_blank" rel="noopener noreferrer">
                                    terms of service
                                </a>.
                            </span>
                        </div>
                        {error && <p style={styles.error}>{error}</p>}
                        {successMessage && <p style={styles.success}>{successMessage}</p>}

                        {sentVerificationCode && (
                        <>
                            <label style={styles.label}>Verification Code</label>
                            <input
                            type="text"
                            style={styles.input}
                            value={enteredCode}
                            onChange={(e) => setEnteredCode(e.target.value)}
                            />
                        </>
                        )}

                        {!sentVerificationCode && (
                            <button
                                style={{ 
                                    ...styles.button, 
                                    opacity: isButtonDisabled ? 0.5 : 1, 
                                    cursor: isButtonDisabled ? "not-allowed" : "pointer" 
                                }} 
                                onClick={handleSendCode}
                                disabled={isButtonDisabled}
                            >
                                {isButtonDisabled ? `Submit (${countdown}s)` : "Submit"}
                            </button>
                        )}

                        {sentVerificationCode && (
                        <div>
                        <button 
                            style={{ 
                                ...styles.button, 
                                opacity: isSubmitting ? 0.5 : 1, 
                                cursor: isSubmitting ? "not-allowed" : "pointer" 
                            }} 
                            onClick={handleBookingSubmit} //
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit Request"}
                        </button>

                        <button 
                                style={{ 
                                    ...styles.button, 
                                    opacity: isButtonDisabled ? 0.5 : 1, 
                                    cursor: isButtonDisabled ? "not-allowed" : "pointer" ,
                                    marginLeft: "50px",
                                    backgroundColor: "grey"
                                }} 
                                onClick={handleSendCode}
                                disabled={isButtonDisabled} // Disable the button while the countdown is active
                            >
                                {isButtonDisabled ? `Request Code Again (${countdown}s)` : "Request Code Again"}
                            </button>
                        </div>
                        )}
                        
                        
                    </div>
                )}
            </div>
            <div style={styles.footer}>
                <p>Powered by 
                {" "}
                <a href={`${CONFIG.FRONTEND_URL}/`} target="_blank" rel="noopener noreferrer">Rili Day</a>
                {" "}|{" "}
                <a href={`${CONFIG.FRONTEND_URL}/privacy`} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                {" "}|{" "}
                <a href={`${CONFIG.FRONTEND_URL}/terms`} target="_blank" rel="noopener noreferrer">Terms of Service</a>
                </p>
            </div>
        </div>
    );
};

const styles = {
    container: { padding: "20px", maxWidth: "800px", margin: "0 auto" },
    title: { fontSize: "32px", marginBottom: "20px" },
    description: { 
        fontSize: "16px", 
        marginBottom: "20px",
        whiteSpace: "pre-line",  // Preserves new lines
        textAlign: "justify"      // Justifies text
    },    
    calendarSection: { marginBottom: "30px" },
    bookingSection: { marginTop: "20px" },
    label: { display: "block", marginBottom: "5px", fontWeight: "bold" },
    input: { display: "block", width: "80%", padding: "10px", marginBottom: "10px", borderRadius: "5px", border: "1px solid #ccc" },
    button: { 
        padding: "10px 20px", 
        backgroundColor: "#2c3e50", 
        color: "#fff", 
        border: "none", 
        borderRadius: "5px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        '&:hover': {
            backgroundColor: "#1a252f",
            transform: "translateY(-2px)",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)"
        }
    },
    error: { color: "red", marginTop: "10px" },
    success: { color: "green", marginTop: "10px" },
    colorBlock: {
        display: "inline-block",
        width: "40px",
        height: "15px", 
        backgroundColor: "rgba(17, 129, 0, 0.338)",
        borderRadius: "3px",
        border: "",
        margin: "0 5px"
    },
    startPrompt: { 
        display: "block", 
        marginTop: "60px", 
        marginBottom: "30px",
        fontWeight: "bold" 
    },
    sectionTitle: {
        fontSize: '24px',
        marginBottom: '20px',
        color: '#2c3e50',
    },
    servicesList: {
        display: 'grid',
        gap: '20px',
        marginBottom: '30px',
    },
    serviceCard: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '10px',
        boxShadow: '0 0 4px rgba(0, 0, 0, 0.1)',
        transition: 'transform 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
            backgroundColor: '#f8f9fa',
        }
    },
    serviceHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
    },
    serviceInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    serviceName: {
        margin: 0,
        fontWeight: 'bold',
        color: '#2c3e50',
    },
    serviceDuration: {
        fontSize: '14px',
        color: '#666',
        backgroundColor: '#f0f0f0',
        padding: '4px 12px',
        borderRadius: '12px',
        marginLeft: 'auto',
    },
    serviceDescription: {
        marginTop: '10px',
        marginBottom: '0',
        fontSize: '15px',
        color: '#555',
        lineHeight: '1.6',
    },
    footer: {
        marginTop: "50px",
        padding: "0px",
        backgroundColor: "rgb(226, 218, 195)",
        textAlign: "center",
        width: "100%",  
        display: "flex",
        justifyContent: "center",
        fontSize: "12px",
        height: "40px"
    }
    
};
const customSelectStyles = {
    control: (base) => ({
        ...base,
        padding: "5px",
        borderRadius: "5px",
        border: "1px solid #ccc",
        fontSize: "16px"
    })
};

export default PublicPage;
